import { t } from '@lingui/core/macro';
import { OfferImportContextOrigin, OfferOriginEnum, OrbweaverOrigin } from '@luminovo/api-contracts';
import { assertUnreachable } from '@luminovo/commons';

export function formatOfferOrigin(props: { origin: OfferOriginEnum }): string {
    const { origin } = props;
    switch (origin) {
        case OfferOriginEnum.Manual:
            return t`Manual`;
        case OfferOriginEnum.QuoteRequest:
            return t`Quote Request`;
        case OfferOriginEnum.Farnell:
            return `Farnell API`;
        case OfferOriginEnum.Newark:
            return `Newark API`;
        case OfferOriginEnum.Element14:
            return `element14 API`;
        case OfferOriginEnum.Octopart:
            return `Octopart`;
        case OfferOriginEnum.TrustedParts:
            return `TrustedParts`;
        case OfferOriginEnum.Buerklin:
            return `Bürklin API`;
        case OfferOriginEnum.Tme:
            return `TME API`;
        case OfferOriginEnum.Weltron:
            return `Weltron API`;
        case OfferOriginEnum.Mouser:
            return `Mouser API`;
        case OfferOriginEnum.MyArrow:
            return `MyArrow API`;
        case OfferOriginEnum.Alfatec:
            return `Alfatec API`;
        case OfferOriginEnum.Arrow:
            return `Arrow API`;
        case OfferOriginEnum.Schukat:
            return `Schukat API`;
        case OfferOriginEnum.Gudeco:
            return `Gudeco API`;
        case OfferOriginEnum.Future:
            return `Future Electronics API`;
        case OfferOriginEnum.Greenchips:
            return `GREENCHIPS API`;
        case OfferOriginEnum.Rutronik:
            return `Rutronik API`;
        case OfferOriginEnum.AvnetAbacus:
            return `Avnet EMEA (Abacus) API`;
        case OfferOriginEnum.AvnetApac:
            return `Avnet APAC API`;
        case OfferOriginEnum.AvnetSilica:
            return `Avnet EMEA (Silica) API`;
        case OfferOriginEnum.AvnetEbv:
            return `Avnet EMEA (EBV) API`;
        case OfferOriginEnum.AvnetUsa:
            return `Avnet USA API`;
        case OfferOriginEnum.Digikey:
            return `DigiKey API`;
        case OfferOriginEnum.Consigned:
            return t`Consigned`;
        case OfferOriginEnum.Import:
            // Origin-only label (filter/dropdown use, or an imported offer with no
            // resolved context). The import-context name is a sibling on the offer,
            // not on `origin` (INT-1204), so the `(name)` is added by
            // `formatOfferOriginWithContext` when a concrete offer with a name is in
            // hand. INT-1767 would move the name back onto the origin.
            return t`Import (No context)`;
        case OfferOriginEnum.Orbweaver:
            return (props as unknown as OrbweaverOrigin).datasource_name ?? 'Orbweaver';
        case OfferOriginEnum.WuerthEisos:
            return `Würth Elektronik eiSos`;
        case OfferOriginEnum.Wuerth:
            return `Würth API`;
        case OfferOriginEnum.BetaLayout:
            return `Beta Layout API`;
        case OfferOriginEnum.SafePcb:
            return `Safe-PCB API`;
        case OfferOriginEnum.IBRRingler:
            return `IBR Ringler API`;
        case OfferOriginEnum.APCT:
            return `APCT API`;
        case OfferOriginEnum.Gatema:
            return `Getema API`;
        case OfferOriginEnum.Alba:
            return `Alba API`;
        case OfferOriginEnum.MultiCB:
            return `MultiCB API`;
        case OfferOriginEnum.Stackrate:
            return `Pricing Console`;
        case OfferOriginEnum.Lcsc:
            return `LCSC API`;
        case OfferOriginEnum.Eve:
            return `EVE API`;
        case OfferOriginEnum.Ti:
            return `TI API`;
        case OfferOriginEnum.Tti:
            return `TTI API`;
        case OfferOriginEnum.Heilind:
            return `Heilind API`;
        case OfferOriginEnum.Hexachip:
            return `Hexachip API`;
        case OfferOriginEnum.Samtec:
            return `Samtec API`;
        case OfferOriginEnum.Blume:
            return `Blume Elektronik API`;
        case OfferOriginEnum.Rochester:
            return `Rochester Electronics API`;
        case OfferOriginEnum.AlElektronik:
            return `AL-Elektronik API`;
        case OfferOriginEnum.Master:
            return `Master Electronics API`;
        case OfferOriginEnum.Unikey:
            return `Unikey API`;
        case OfferOriginEnum.OnlineComponents:
            return `Online Components API`;
        case OfferOriginEnum.QuestComponents:
            return `Quest Components API`;
        case OfferOriginEnum.RapidElectronics:
            return `Rapid Electronics API`;
        case OfferOriginEnum.RsComponents:
            return `RS Components API`;
        case OfferOriginEnum.RmComponents:
            return `RM Components API`;
        case OfferOriginEnum.SiliconExpert:
            return `SiliconExpert API`;
        case OfferOriginEnum.Sos:
            return `SOS Electronic API`;
        case OfferOriginEnum.ChipCart:
            return `ChipCart API`;
        case OfferOriginEnum.Corestaff:
            return `CoreStaff API`;
        case OfferOriginEnum.Sourcengine:
            return `Sourcengine API`;
        case OfferOriginEnum.Venkel:
            return `Venkel API`;
        case OfferOriginEnum.WinSource:
            return 'Win Source API';
        case OfferOriginEnum.IcHub:
            return 'icHub API';
        case OfferOriginEnum.Ecomal:
            return 'Ecomal API';
        case OfferOriginEnum.Wittig:
            return 'Wittig Electronic API';
        case OfferOriginEnum.Telcona:
            return 'Telcona API';
        case OfferOriginEnum.Kruse:
            return 'Kruse API';
        case OfferOriginEnum.Ineltro:
            return 'Ineltro API';
        case OfferOriginEnum.AtlantikNetworxx:
            return 'Atlantik Networxx API';
        case OfferOriginEnum.Bossard:
            return 'Bossard API';
        case OfferOriginEnum.Mev:
            return 'MEV Elektronik API';
        default:
            assertUnreachable(origin);
    }
}

/**
 * Format an offer's origin *including* its resolved import-context name.
 *
 * Prefer this over `formatOfferOrigin` wherever a concrete offer is in hand
 * (tables, drawers, comparison cards). Filter/dropdown labels, which only have a
 * bare origin discriminant, keep using `formatOfferOrigin`.
 *
 * Why this is a separate function taking the whole offer, rather than
 * `formatOfferOrigin(origin, name?)`: the resolved name is NOT part of the
 * `origin` object. INT-1204 removed the free-text `context` from the domain
 * `OfferOrigin`, and the backend now sends the resolved name as a sibling field
 * (`import_context_name`) on the offer response. A loose optional second argument
 * would be weakly typed (origin and name uncoupled, so a caller could silently
 * forget it); taking the whole offer keeps them coupled in one type.
 *
 * The intended end-state is INT-1767: move `context_name` back *into* the origin
 * wire DTO. Once that lands, this helper and the `import_context_name` sibling can
 * be deleted, and callers can go back to `formatOfferOrigin(origin)` reading the
 * name straight off the origin.
 */
export function formatOfferImportContextOrigin(origin: OfferImportContextOrigin): string {
    switch (origin) {
        case 'CsvIntegration':
            return t`CSV integration`;
        case 'ApiIntegration':
            return t`API integration`;
        case 'ManualFileUpload':
            return t`Manual file upload`;
        default:
            assertUnreachable(origin);
    }
}

export function formatOfferOriginWithContext(offer: {
    origin: { origin: OfferOriginEnum };
    import_context_name?: string | null;
}): string {
    if (offer.origin.origin === OfferOriginEnum.Import && offer.import_context_name) {
        // Bind to a `context` identifier so the i18n placeholder stays `{context}`,
        // preserving the existing `Import ({context})` message id and its translations.
        const context = offer.import_context_name;
        return t`Import (${context})`;
    }
    return formatOfferOrigin(offer.origin);
}
